import { css } from "@emotion/css";
import { Checkbox, RadioButtonNew, RadioGroupNew } from "@octopusdeploy/design-system-components";
import { StringHelper } from "@octopusdeploy/design-system-octopus-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { GitCommitMethods, type ArgoCDGitCommitProperties } from "@octopusdeploy/legacy-action-properties";
import { unbindParameterName, type EnvironmentV2Resource, type PersistenceSettings, type ProcessTemplateParameterResource, type ProcessType } from "@octopusdeploy/octopus-server-client";
import { useMemo } from "react";
import { isProcessOrProjectTemplateProcessType } from "~/areas/projects/components/Process/types";
import { ArgoCDEnvironmentMultiSelect } from "~/components/Actions/argocd/ArgoCDEnvironmentMultiSelect";
import ExpandableFormSection from "~/components/form/Sections/ExpandableFormSection";
import Summary from "~/components/form/Sections/Summary";
const ContentPanel = (props: ArgoCDCommitMethodProps) => {
    const { properties, setProperties, environments, processType, persistenceSettings } = props;
    const setCommitMethod = (value: string | boolean) => {
        if (typeof value === "boolean") {
            value = value ? GitCommitMethods.PullRequest : GitCommitMethods.DirectCommit;
        }
        let updateObject: Partial<ArgoCDGitCommitProperties> = { "Octopus.Action.ArgoCD.CommitMethod": value };
        if (value === GitCommitMethods.PullRequest) {
            updateObject = {
                ...updateObject,
                "Octopus.Action.ArgoCD.PullRequest.Environments": undefined,
                "Octopus.Action.ArgoCD.PullRequest.EnvironmentsTemplateParameter": undefined,
            };
        }
        setProperties(updateObject);
    };
    const selectedEnvIds = useMemo(() => {
        const value = properties["Octopus.Action.ArgoCD.PullRequest.Environments"];
        if (StringHelper.isNullOrWhiteSpace(value)) {
            return [];
        }
        return value.split(",");
    }, [properties]);
    const onSelectedEnvIdsChanged = (newValue: string[]) => {
        setProperties({ "Octopus.Action.ArgoCD.PullRequest.Environments": newValue.join(",") });
    };
    const onTemplateParameterChanged = (newValue: string | null) => {
        setProperties({ "Octopus.Action.ArgoCD.PullRequest.EnvironmentsTemplateParameter": newValue ?? undefined });
    };
    const commitMethod = properties["Octopus.Action.ArgoCD.CommitMethod"];
    return (<RadioGroupNew label="" value={commitMethod} onChange={setCommitMethod}>
            <RadioButtonNew name={"GitCommitMethods"} value={GitCommitMethods.PullRequest} label={"All environments"}/>
            <RadioButtonNew name={"GitCommitMethods"} value={GitCommitMethods.PerEnvironment} label={"Specific environments"}/>
            {commitMethod === GitCommitMethods.PerEnvironment && (<div className={styles.specificEnvironmentsContainer}>
                    <ArgoCDEnvironmentMultiSelect processType={processType} persistenceSettings={persistenceSettings} environments={environments} selectedEnvironmentIds={selectedEnvIds} onSelectedEnvironmentIdsChanged={onSelectedEnvIdsChanged} templateParameter={properties["Octopus.Action.ArgoCD.PullRequest.EnvironmentsTemplateParameter"]} onTemplateParameterChanged={onTemplateParameterChanged}/>
                </div>)}
        </RadioGroupNew>);
};
type ArgoCDCommitMethodProps = {
    expandedByDefault?: boolean;
    environments: EnvironmentV2Resource[] | undefined;
    processType: ProcessType | undefined;
    persistenceSettings: PersistenceSettings | undefined;
    properties: ArgoCDGitCommitProperties;
    setProperties: (properties: Partial<ArgoCDGitCommitProperties>, initialize?: boolean, callback?: () => void) => void;
    onParameterAdded?: (parameters: ProcessTemplateParameterResource[]) => void;
};
export function ArgoCDGitCommitMethodFormSection(props: ArgoCDCommitMethodProps) {
    const { expandedByDefault, properties, setProperties, processType, ...rest } = props;
    const mode = properties["Octopus.Action.ArgoCD.CommitMethod"];
    const envs = properties["Octopus.Action.ArgoCD.PullRequest.Environments"];
    const environmentsTemplateParameter = properties["Octopus.Action.ArgoCD.PullRequest.EnvironmentsTemplateParameter"];
    const summary = useMemo(() => {
        switch (mode) {
            case GitCommitMethods.PullRequest:
                return Summary.summary("Create pull requests for all environments");
            case GitCommitMethods.PerEnvironment:
                if (isProcessOrProjectTemplateProcessType(processType)) {
                    return Summary.summary(<span>
                            Create pull requests for environments specified by the parameter <strong>{unbindParameterName(environmentsTemplateParameter)}</strong>
                        </span>);
                }
                else {
                    const envCount = (envs || "").split(",").filter((x) => !StringHelper.isNullOrWhiteSpace(x)).length;
                    return Summary.summary(`Create pull requests for ${envCount} environment${envCount === 1 ? "" : "s"}`);
                }
            case GitCommitMethods.DirectCommit:
            default:
                return Summary.default("Direct commit for all environments");
        }
    }, [envs, mode, processType, environmentsTemplateParameter]);
    const setCommitMethod = (enabled: boolean) => {
        let updateObject: Partial<ArgoCDGitCommitProperties> = { "Octopus.Action.ArgoCD.CommitMethod": enabled ? GitCommitMethods.PullRequest : GitCommitMethods.DirectCommit };
        if (!enabled) {
            updateObject = {
                ...updateObject,
                "Octopus.Action.ArgoCD.PullRequest.Environments": undefined,
                "Octopus.Action.ArgoCD.PullRequest.EnvironmentsTemplateParameter": undefined,
            };
        }
        setProperties(updateObject);
    };
    const arePullRequestsEnabled = !StringHelper.isNullOrWhiteSpace(mode) && mode !== GitCommitMethods.DirectCommit;
    return (<ExpandableFormSection errorKey="Octopus.Action.ArgoCD.CommitMethod" title="Git Commit Method" summary={summary} help={"Select the environments where Octopus should open pull requests or commit directly."} isExpandedByDefault={props.expandedByDefault}>
            <Checkbox value={arePullRequestsEnabled} onChange={setCommitMethod} label={"Open pull requests"}/>
            {arePullRequestsEnabled && (<div className={styles.contentPanelContainer}>
                    <ContentPanel properties={properties} setProperties={setProperties} processType={processType} {...rest}/>
                </div>)}
        </ExpandableFormSection>);
}
const styles = {
    contentPanelContainer: css({
        paddingLeft: space[12],
    }),
    specificEnvironmentsContainer: css({
        marginTop: space[12],
    }),
};
